import React, { useCallback, useEffect, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  SessionBoxInfo,
  CenteredLoader,
  Button,
  ProgressLine,
  ButtonIcon,
  Toggle,
} from 'components';
import { Trans, useTranslation } from 'react-i18next';
import {
  Session,
  useRecruitmentInvitationQueue,
  useRecruitment,
  InvitationQueueStatus,
  useUpdateInvitationQueue,
  useInvitationQueueSpeedInfo,
  useSeveralQueuesRepartition,
} from '@app/api';
import { config } from 'config';
import { CriteriaConfigTable } from './components/CriteriaConfigTable';
import { QueueSearchConfig } from './components/QueueSearchConfig';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase * 4,
    overflow: 'auto',
    height: '100%',
  },
  boxesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginRight: -theme.marginBase * 2,
  },
  box: {
    flex: 1,
    marginRight: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
  },
  statusLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  playPauseContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },

  buttons: {
    display: 'flex',
    justifyContent: 'center',
    marginRight: -theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
    '& > *': {
      marginRight: theme.marginBase * 2,
    },
  },
  criteriaTitle: {
    ...theme.fonts.h3,
  },
  criteria: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase * 2,
  },
  searchesTitle: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 2,
  },
  queueRepartitionList: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    marginBottom: theme.marginBase * 4,
  },
  toggleContainer: {
    width: 'auto',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.marginBase,
  },
}));

interface Props {
  session: Session;
}

export const InvitationQueueDashboard = ({ session }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { data: recruitment } = useRecruitment(session.recruitmentId);
  const { data: queue, isLoading } = useRecruitmentInvitationQueue(
    session.recruitmentId,
  );

  const { data: queueConfigs } = useSeveralQueuesRepartition(queue?.id);
  const [criteriaWeightWhitelist, setCriteriaWeightWhitelist] = useState<
    undefined | string[]
  >(undefined);

  const { mutate: updateQueue } = useUpdateInvitationQueue(queue?.id);
  const { data: speedInformation } = useInvitationQueueSpeedInfo(queue?.id);
  const [includeAcceptedParticipations, setIncludeAcceptedParticipations] =
    useState(!queue?.excludeAcceptedParticipations);

  const play = useCallback(
    () => updateQueue({ status: InvitationQueueStatus.ACTIVE }),
    [updateQueue],
  );
  const pause = useCallback(
    () => updateQueue({ status: InvitationQueueStatus.PAUSED }),
    [updateQueue],
  );
  const stop = useCallback(
    () => updateQueue({ status: InvitationQueueStatus.STOPPED }),
    [updateQueue],
  );

  useEffect(() => {
    setIncludeAcceptedParticipations(!queue?.excludeAcceptedParticipations);
  }, [queue?.excludeAcceptedParticipations, setIncludeAcceptedParticipations]);
  const handleToggle = useCallback(async () => {
    const newValue = !includeAcceptedParticipations;
    setIncludeAcceptedParticipations(newValue);
    try {
      updateQueue({ excludeAcceptedParticipations: !newValue });
    } catch (error) {
      setIncludeAcceptedParticipations(!newValue);
    }
  }, [includeAcceptedParticipations, updateQueue]);

  if (isLoading) {
    return <CenteredLoader />;
  }
  if (!queue) {
    return <div></div>;
  }

  return (
    <div className={classes.container}>
      <div className={classes.boxesContainer}>
        {recruitment && (
          <SessionBoxInfo
            color="blue"
            title={t('sessions.details.invitationQueue.recruitmentProgress')}
            className={classes.box}
            icon="ri-user-3-line"
          >
            <div className={classes.statusLine}>
              {t('sessions.details.invitationQueue.recruitmentProgressText', {
                missing: speedInformation?.missingTotal || 0,
              })}
            </div>
            <Toggle
              containerClass={classes.toggleContainer}
              noMargin
              label={
                <span>
                  {t(
                    'sessions.details.invitationQueue.includeAcceptedParticipationsLabel',
                  )}
                </span>
              }
              tooltip={t(
                'sessions.details.invitationQueue.includeAcceptedParticipationsDescription',
              )}
              checked={includeAcceptedParticipations}
              onToggle={handleToggle}
            />
          </SessionBoxInfo>
        )}
        <SessionBoxInfo
          color={
            queue.status === InvitationQueueStatus.ACTIVE ? 'green' : 'red'
          }
          title={t('sessions.details.invitationQueue.status')}
          className={classes.box}
          icon="ri-eye-line"
        >
          <div className={classes.statusLine}>
            {queue.status}
            <div className={classes.playPauseContainer}>
              {queue?.status !== InvitationQueueStatus.ACTIVE && (
                <ButtonIcon
                  icon="ri-play-line"
                  color="white"
                  onClick={play}
                  title="Play"
                  variant="border"
                />
              )}
              {queue?.status !== InvitationQueueStatus.PAUSED && (
                <ButtonIcon
                  icon="ri-pause-line"
                  color="white"
                  onClick={pause}
                  title="Pause"
                  variant="border"
                />
              )}
              {queue?.status !== InvitationQueueStatus.STOPPED && (
                <ButtonIcon icon="ri-stop-line" color="ink" onClick={stop} />
              )}
            </div>
          </div>
        </SessionBoxInfo>
        <SessionBoxInfo
          color="purple"
          title={t('sessions.details.invitationQueue.speed')}
          className={classes.box}
          icon="ri-calendar-event-line"
        >
          <Trans
            i18nKey="sessions.details.invitationQueue.speedText"
            values={speedInformation || { speed: 0, total: 0, missingTotal: 0 }}
          />
        </SessionBoxInfo>
        <SessionBoxInfo
          color="blue"
          title={t('sessions.details.invitationQueue.queueLength')}
          className={classes.box}
          icon="ri-arrow-right-line"
        >
          <ProgressLine
            current={queue.numberOfInvitationSent}
            max={queue.numberOfInvitationSent + queue.remainingResults}
            oneline
          />
        </SessionBoxInfo>
      </div>
      <div className={classes.buttons}>
        <Button
          href={`${config.FOREST_URL_PREFIX}/invitationQueue/index/record/invitationQueue/${queue.id}/details`}
          small
          variant="muted"
          withNextArrow
          target="_blank"
        >
          <Trans i18nKey="sessions.details.invitationQueue.forest" />
        </Button>
      </div>

      {queueConfigs?.length && queueConfigs.length > 1 && (
        <>
          <h2 className={classes.searchesTitle}>
            {t('sessions.details.invitationQueue.searches')}
          </h2>
          <div className={classes.queueRepartitionList}>
            {queueConfigs.map((config) => (
              <QueueSearchConfig
                config={config}
                totalToInviteDaily={speedInformation?.total}
                key={config.criteriaWeightWhitelist.join('-')}
                onClick={setCriteriaWeightWhitelist}
                selectedWhiteList={criteriaWeightWhitelist}
              />
            ))}
          </div>
        </>
      )}

      <h2 className={classes.criteriaTitle}>
        {t('sessions.details.invitationQueue.criteria')}
      </h2>
      <CriteriaConfigTable
        queue={queue}
        selectedWhiteList={criteriaWeightWhitelist}
      />
    </div>
  );
};
